(() => {
  const TradeCore = window.FereGoldTradeCore;
  if (!TradeCore) {
    console.error('[FERE Gold price] Gold trade core is unavailable.');
    return;
  }
  if (typeof window.fereGoldJourneyInit === 'function') {
    window.fereGoldJourneyInit(document);
    return;
  }

  const PROVIDERS = {
    goldApi: {
      id: 'gold_api',
      label: 'Gold-API.com',
      homeUrl: 'https://gold-api.com/',
      endpoint: 'https://api.gold-api.com/price/XAU',
    },
    xaus: {
      id: 'xaus',
      label: 'XAUS.com',
      homeUrl: 'https://xaus.com/',
      endpoint: 'https://xaus.com/api/v1/spot?compact=1&currency=USD&unit=oz',
    },
  };
  const TROY_OUNCE_GRAMS = TradeCore.TROY_OUNCE_GRAMS;
  const CASH_FACTOR = TradeCore.CASH_FACTOR;
  const TRADE_FACTOR = TradeCore.TRADE_FACTOR;
  const REFRESH_INTERVAL_MS = TradeCore.MAX_FRESH_AGE_MS;
  const REQUEST_TIMEOUT_MS = 8000;
  const MAX_FRESH_AGE_MS = TradeCore.MAX_FRESH_AGE_MS;
  // This ceiling intentionally covers ordinary weekend market closure without
  // presenting an older quote as usable market data indefinitely.
  const MAX_STALE_AGE_MS = TradeCore.MAX_QUOTE_AGE_MS;
  const PRIMARY_RETRY_DELAYS_MS = [2000, 8000];
  // A failed XAUS fallback is not retried on each ten-minute check. This also
  // protects against the provider's current cross-origin 503 response, whose
  // usage_exceeded body can be hidden from browser JavaScript by CORS.
  const XAUS_FAILURE_COOLDOWN_MS = 60 * 60 * 1000;
  const RESUME_DEDUPE_MS = 1000;
  const MAX_CALCULATOR_WEIGHT_GRAMS = TradeCore.MAX_WEIGHT_GRAMS;
  const VALID_SPOT_RANGE_USD = [500, 10000];
  const KARATS = TradeCore.KARATS;

  let xausCooldownUntil = 0;
  let xausDisabledForSession = false;

  const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
  const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

  class GoldPriceError extends Error {
    constructor(code, options = {}) {
      super(code);
      this.name = 'GoldPriceError';
      this.code = code;
      this.provider = options.provider || '';
      this.transient = Boolean(options.transient);
      this.status = Number.isFinite(options.status) ? options.status : 0;
    }
  }

  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const moneyFromCents = (cents) => money.format(Number(cents) / 100);
  const displayOrUnknown = (cents) => Number.isSafeInteger(cents) ? `${moneyFromCents(cents)} USD` : 'Unknown';
  const displayWithCentsOrUnknown = (cents) => Number.isSafeInteger(cents)
    ? `${moneyFromCents(cents)} USD (${cents} cents)`
    : 'Unknown';
  const canonicalSpotUsd = (value) => Number.isFinite(Number(value))
    ? Number(value).toFixed(8).replace(/\.?0+$/, '')
    : '';
  const scrollIntoViewRespectingMotion = (element, block = 'start') => {
    if (!element) return;
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block });
  };

  const setValue = (root, key, value) => {
    const element = root.querySelector(`[data-fere-contact-field="${key}"]`);
    if (element) element.value = value;
  };

  const setText = (root, selector, value) => {
    const element = root.querySelector(selector);
    if (element) element.textContent = value;
  };

  const setTextAll = (root, selector, value) => {
    root.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  };

  const setAttribute = (root, selector, name, value) => {
    const element = root.querySelector(selector);
    if (element) element.setAttribute(name, value);
  };

  const GOLD_COPY = {
    "enterWeight": "Enter Weight",
    "pricePending": "Price update pending",
    "priceRequest": "Price on request",
    "sourcePending": "Source pending",
    "marketPending": "Current market pricing is being refreshed. Contact us for a current quote.",
    "inputPrompt": "Enter a weight to calculate an estimate.",
    "spotPending": "Gold spot price update pending",
    "karatPending": "Gold price by karat update pending",
    "viewAll": "View All Engagement Rings",
    "showFewer": "Show Fewer Rings",
    "currentEstimatePending": "Gold details preserved \u00b7 current price update pending"
};
  const goldCopy = (root, key) => root?.dataset?.[`gold${key[0].toUpperCase()}${key.slice(1)}`] || GOLD_COPY[key];
  const pendingEstimateLabel = (root) => {
    const weight = Number(root.querySelector('[data-fere-gold-calc-weight]')?.value);
    return goldCopy(root, Number.isFinite(weight) && weight >= TradeCore.MIN_WEIGHT_GRAMS && weight <= MAX_CALCULATOR_WEIGHT_GRAMS ? 'pricePending' : 'enterWeight');
  };

  const updateProviderAttribution = (root, quote = null) => {
    const element = root.querySelector('[data-fere-gold-provider]');
    if (!element) return;
    if (!quote) {
      element.textContent = goldCopy(root, 'sourcePending');
      element.removeAttribute('href');
      return;
    }

    const provider = quote.providerId === PROVIDERS.goldApi.id
      ? PROVIDERS.goldApi
      : quote.providerId === PROVIDERS.xaus.id
        ? PROVIDERS.xaus
        : null;
    element.textContent = provider?.label || quote.providerLabel || quote.source || goldCopy(root, 'sourcePending');
    if (provider) element.setAttribute('href', provider.homeUrl);
    else element.removeAttribute('href');
  };

  const setDiagnostic = (root, code, details = {}) => {
    root.dataset.goldDiagnosticCode = code;
    root.dataset.goldDiagnosticProvider = details.provider || '';
    root.dataset.goldDiagnosticAttempt = String(details.attempt || 0);

    if (Number.isFinite(details.httpStatus) && details.httpStatus > 0) {
      root.dataset.goldDiagnosticHttpStatus = String(details.httpStatus);
    } else {
      delete root.dataset.goldDiagnosticHttpStatus;
    }

    if (Number.isFinite(details.quoteAgeMs)) {
      root.dataset.goldDiagnosticQuoteAgeSeconds = String(Math.max(0, Math.round(details.quoteAgeMs / 1000)));
    } else {
      delete root.dataset.goldDiagnosticQuoteAgeSeconds;
    }

    console.info('[FERE Gold price]', {
      code,
      provider: details.provider || '',
      attempt: details.attempt || 0,
      httpStatus: Number.isFinite(details.httpStatus) ? details.httpStatus : 0,
    });
  };

  const updateStateCopy = (root, marketState) => {
    const copy = {
      fresh: {
        heroEyebrow: 'LIVE GOLD PRICES',
        heroSummary: 'See what your gold could contribute toward a ring you love, or keep the 85% cash option.',
        liveNote: 'Live market pricing',
        spotLabel: 'Live Gold Spot',
        stateLabel: 'Live',
        spotAria: 'Current live gold spot price',
        valuesAria: 'Live gold price by karat',
        marketSubtitle: 'Live Gold Value',
        gridAria: 'Live market, estimated cash, and estimated trade value per gram by karat',
        calculatorTitle: 'Calculate your live gold value',
        calculatorReference: 'Reference live gold value',
        marketDescription: 'Reference value calculated from the current live gold price, karat, and gold weight.',
      },
      stale: {
        heroEyebrow: 'LAST AVAILABLE GOLD PRICE',
        heroSummary: 'Review the last available estimate, keep your gold details, and ask FERE to confirm current cash or trade value.',
        liveNote: 'Last available market pricing',
        spotLabel: 'Last available market price',
        stateLabel: 'Last available',
        spotAria: 'Last available gold market price; not live',
        valuesAria: 'Last available gold market price by karat',
        marketSubtitle: 'Last Available Gold Value',
        gridAria: 'Last available market, estimated cash, and estimated trade value per gram by karat',
        calculatorTitle: 'Calculate from the last available gold price',
        calculatorReference: 'Reference last available gold value',
        marketDescription: 'Reference value calculated from the displayed last available gold price, karat, and gold weight. This is not a live price.',
      },
      unavailable: {
        heroEyebrow: 'GOLD PRICE INFORMATION',
        heroSummary: 'Enter your gold details now, then ask FERE to confirm current cash or trade value when market pricing is available.',
        liveNote: 'Market pricing status',
        spotLabel: 'Gold Spot Price',
        stateLabel: goldCopy(root, 'pricePending'),
        spotAria: goldCopy(root, 'spotPending'),
        valuesAria: goldCopy(root, 'karatPending'),
        marketSubtitle: 'Gold Reference Value',
        gridAria: 'Gold market, estimated cash, and estimated trade value per gram by karat',
        calculatorTitle: 'Calculate your gold value',
        calculatorReference: 'Reference gold value',
        marketDescription: 'Reference value is calculated from the displayed gold price, karat, and gold weight when market pricing is available.',
      },
    }[marketState];

    setText(root, '[data-fere-gold-hero-eyebrow]', copy.heroEyebrow);
    setText(root, '[data-fere-gold-hero-summary]', copy.heroSummary);
    setText(root, '[data-fere-gold-live-note]', copy.liveNote);
    setText(root, '[data-fere-gold-spot-label]', copy.spotLabel);
    setText(root, '[data-fere-gold-state-label]', copy.stateLabel);
    setText(root, '[data-fere-gold-market-subtitle]', copy.marketSubtitle);
    setText(root, '[data-fere-gold-calculator-title]', copy.calculatorTitle);
    setText(root, '[data-fere-gold-calc-reference]', copy.calculatorReference);
    setText(root, '[data-fere-gold-market-description]', copy.marketDescription);
    setAttribute(root, '[data-fere-gold-spot-region]', 'aria-label', copy.spotAria);
    setAttribute(root, '[data-fere-gold-values-region]', 'aria-label', copy.valuesAria);
    setAttribute(root, '[data-fere-gold-grid]', 'aria-label', copy.gridAria);
  };

  const clearCalculator = (root, state, message) => {
    if (root.fereGoldSubmissionLocked) return;
    const calculator = root.querySelector('[data-fere-gold-calculator]');
    if (calculator) calculator.dataset.state = state;
    root.fereGoldSnapshot = null;
    setText(root, '[data-fere-gold-calc-market]', pendingEstimateLabel(root));
    setText(root, '[data-fere-gold-calc-cash]', pendingEstimateLabel(root));
    setText(root, '[data-fere-gold-calc-trade]', pendingEstimateLabel(root));
    setText(root, '[data-fere-gold-calc-status]', message);
    renderTradeJourney(root);
    updateContactPayload(root);
  };

  const clearPriceExpiry = (root) => {
    if (root.fereGoldExpiryTimer) window.clearTimeout(root.fereGoldExpiryTimer);
    root.fereGoldExpiryTimer = null;
  };

  const renderCalculator = (root) => {
    if (root.fereGoldSubmissionLocked) return;
    const calculator = root.querySelector('[data-fere-gold-calculator]');
    const karatInput = root.querySelector('[data-fere-gold-calc-karat]');
    const weightInput = root.querySelector('[data-fere-gold-calc-weight]');

    if (!calculator || !karatInput || !weightInput) return;
    if (root.fereFormKaratOverride === true) {
      clearCalculator(root, 'empty', 'Choose a supported karat in the calculator for an online estimate, or send the item as not sure.');
      return;
    }
    const rawWeight = weightInput.value.trim();
    if (!rawWeight) {
      clearCalculator(root, 'empty', goldCopy(root, 'inputPrompt'));
      return;
    }
    if (!root.fereLastQuote) {
      clearCalculator(root, 'unavailable', goldCopy(root, 'marketPending'));
      return;
    }

    let assessed;
    try {
      assessed = assessQuote(root.fereLastQuote);
    } catch (_error) {
      clearCalculator(root, 'unavailable', 'The saved market price is no longer usable. Keep your details and ask FERE for a current evaluation.');
      return;
    }

    const estimate = TradeCore.calculateEstimate({
      spotUsd: assessed.spot,
      weightGrams: rawWeight,
      karat: karatInput.value,
    });
    if (!estimate) {
      const numericWeight = Number(rawWeight);
      const message = Number.isFinite(numericWeight) && numericWeight > MAX_CALCULATOR_WEIGHT_GRAMS
        ? 'For larger quantities, please contact FERE Jewelers for a direct evaluation.'
        : `Enter a valid weight from ${TradeCore.MIN_WEIGHT_GRAMS} to ${MAX_CALCULATOR_WEIGHT_GRAMS.toLocaleString('en-US')} grams.`;
      clearCalculator(root, 'error', message);
      return;
    }

    calculator.dataset.state = 'ready';
    root.fereGoldSnapshot = Object.freeze({ ...estimate, quote: Object.freeze({ ...assessed }) });
    setText(root, '[data-fere-gold-calc-market]', moneyFromCents(estimate.marketCents));
    setText(root, '[data-fere-gold-calc-cash]', moneyFromCents(estimate.cashCents));
    setText(root, '[data-fere-gold-calc-trade]', moneyFromCents(estimate.tradeCents));
    const sourceLabel = root.dataset.marketState === 'stale' ? ' using the last available market price' : '';
    setText(root, '[data-fere-gold-calc-status]', `Estimate for ${estimate.weightGrams.toLocaleString('en-US')} grams of ${estimate.karat}K gold${sourceLabel}.`);
    syncContactGoldFields(root, estimate);
    renderTradeJourney(root);
    updateContactPayload(root);
  };

  const formatCurrencyCents = (cents, currency) => {
    if (!Number.isSafeInteger(cents) || !/^[A-Z]{3}$/.test(String(currency || ''))) return goldCopy(document.querySelector('[data-fere-live-gold]'), 'priceRequest');
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return goldCopy(document.querySelector('[data-fere-live-gold]'), 'priceRequest');
    }
  };

  const readRingCard = (card) => {
    const select = card?.querySelector('[data-fere-ring-variant]');
    const option = select?.options?.[select.selectedIndex];
    const priceCents = Number(option?.dataset.priceCents);
    if (!option || !/^\d+$/.test(String(option.value || '')) || !Number.isSafeInteger(priceCents) || priceCents < 0) return null;
    return TradeCore.sanitizeRing({
      productId: String(card.dataset.productId || ''),
      variantId: String(option.value),
      sku: String(option.dataset.sku || 'Unknown'),
      title: String(card.dataset.productTitle || 'Unknown ring'),
      options: String(option.dataset.options || '').split(' · ').map((value) => value.trim()).filter(Boolean),
      priceCents,
      currency: String(card.dataset.currency || '').toUpperCase(),
      url: String(option.dataset.url || card.dataset.productUrl || ''),
    });
  };

  const renderRingCard = (root, card) => {
    const ring = readRingCard(card);
    const snapshot = root.fereGoldSnapshot;
    const details = card.querySelectorAll('[data-fere-ring-details]');
    const choose = card.querySelector('[data-fere-ring-choose]');
    if (!ring) {
      if (choose) choose.disabled = true;
      return;
    }

    details.forEach((link) => link.setAttribute('href', ring.url));
    setText(card, '[data-fere-ring-price]', formatCurrencyCents(ring.priceCents, ring.currency));
    const option = card.querySelector('[data-fere-ring-variant]')?.selectedOptions?.[0];
    const cardImage = card.querySelector('.fere-gold-ring-card__image img');
    if (cardImage && option?.dataset.imageUrl) {
      cardImage.src = option.dataset.imageUrl;
      cardImage.srcset = `${option.dataset.imageUrl} 640w`;
      cardImage.alt = option.dataset.imageAlt || ring.title;
    }
    if (!snapshot) {
      setText(card, '[data-fere-ring-credit]', pendingEstimateLabel(root));
      setText(card, '[data-fere-ring-add]', pendingEstimateLabel(root));
      setText(card, '[data-fere-ring-currency-note]', `Price shown in ${ring.currency}. Calculate a current estimate above.`);
    } else if (ring.currency !== 'USD') {
      setText(card, '[data-fere-ring-credit]', 'USD estimate');
      setText(card, '[data-fere-ring-add]', 'Currency confirmation needed');
      setText(card, '[data-fere-ring-currency-note]', `This ring is shown in ${ring.currency}; FERE must confirm a same-currency comparison.`);
    } else {
      const contribution = TradeCore.calculateContribution({ ringPriceCents: ring.priceCents, tradeCents: snapshot.tradeCents });
      setText(card, '[data-fere-ring-credit]', moneyFromCents(snapshot.tradeCents));
      setText(card, '[data-fere-ring-add]', moneyFromCents(contribution.additionalPaymentCents));
      setText(card, '[data-fere-ring-currency-note]', 'USD estimates before applicable tax and in-person verification.');
    }
    if (choose) choose.disabled = false;
    const isSelected = root.fereSelectedCard
      ? root.fereSelectedCard === card && root.fereSelectedRing?.variantId === ring.variantId
      : root.fereSelectedRing?.productId === ring.productId && root.fereSelectedRing?.variantId === ring.variantId;
    card.classList.toggle('is-selected', isSelected);
  };

  const renderSelectedRing = (root) => {
    const panel = root.querySelector('[data-fere-ring-selection]');
    const ring = root.fereSelectedRing;
    if (!panel) return;
    panel.hidden = !ring;
    if (!ring) return;

    const card = (root.fereSelectedCard?.isConnected && root.fereSelectedCard.dataset.productId === ring.productId)
      ? root.fereSelectedCard
      : Array.from(root.querySelectorAll('[data-fere-ring-card]')).find((candidate) => {
        const selectedVariant = candidate.querySelector('[data-fere-ring-variant]')?.value;
        return candidate.dataset.productId === ring.productId && selectedVariant === ring.variantId;
      }) || Array.from(root.querySelectorAll('[data-fere-ring-card]'))
        .find((candidate) => candidate.dataset.productId === ring.productId);
    const selectedOption = card?.querySelector('[data-fere-ring-variant]')?.selectedOptions?.[0];
    const image = card?.querySelector('.fere-gold-ring-card__image img');
    const selectedImage = panel.querySelector('[data-fere-selected-ring-image]');
    const selectedMedia = panel.querySelector('[data-fere-selected-ring-media]');
    const imageUrl = selectedOption?.dataset.imageUrl || image?.currentSrc || image?.src || '';
    if (selectedImage && imageUrl) {
      selectedImage.src = imageUrl;
      selectedImage.alt = selectedOption?.dataset.imageAlt || image?.alt || ring.title;
      if (selectedMedia) selectedMedia.hidden = false;
    } else if (selectedImage) {
      selectedImage.removeAttribute('src');
      selectedImage.alt = '';
      if (selectedMedia) selectedMedia.hidden = true;
    }

    setText(root, '[data-fere-selected-ring-title]', ring.title);
    setText(root, '[data-fere-selected-ring-options]', ring.options.length ? ring.options.join(' · ') : 'Configuration to be confirmed');
    setText(root, '[data-fere-selected-ring-price]', formatCurrencyCents(ring.priceCents, ring.currency));

    const snapshot = root.fereGoldSnapshot;
    const excess = root.querySelector('[data-fere-selected-ring-excess]');
    if (!snapshot) {
      setText(root, '[data-fere-selected-ring-credit]', pendingEstimateLabel(root));
      setText(root, '[data-fere-selected-ring-add]', pendingEstimateLabel(root));
      if (excess) excess.hidden = true;
      return;
    }
    if (ring.currency !== 'USD') {
      setText(root, '[data-fere-selected-ring-credit]', `${moneyFromCents(snapshot.tradeCents)} USD`);
      setText(root, '[data-fere-selected-ring-add]', 'FERE will confirm');
      if (excess) {
        excess.hidden = false;
        excess.textContent = `The ring is shown in ${ring.currency}; no cross-currency subtraction has been made.`;
      }
      return;
    }

    const contribution = TradeCore.calculateContribution({ ringPriceCents: ring.priceCents, tradeCents: snapshot.tradeCents });
    setText(root, '[data-fere-selected-ring-credit]', moneyFromCents(snapshot.tradeCents));
    setText(root, '[data-fere-selected-ring-add]', moneyFromCents(contribution.additionalPaymentCents));
    if (excess) {
      excess.hidden = contribution.unresolvedExcessCents === 0;
      excess.textContent = contribution.unresolvedExcessCents > 0
        ? `${moneyFromCents(contribution.unresolvedExcessCents)} of the estimated credit exceeds this item price. FERE will confirm how any excess is handled.`
        : '';
    }
  };

  const renderTradeJourney = (root) => {
    const snapshot = root.fereGoldSnapshot;
    if (!snapshot) {
      setText(root, '[data-fere-credit-gold]', 'Enter weight and karat above');
      setText(root, '[data-fere-credit-trade]', pendingEstimateLabel(root));
      setText(root, '[data-fere-credit-cash]', pendingEstimateLabel(root));
      setText(root, '[data-fere-credit-extra]', pendingEstimateLabel(root));
    } else {
      setText(root, '[data-fere-credit-gold]', `${snapshot.weightGrams.toLocaleString('en-US')} g · ${snapshot.karat}K`);
      setText(root, '[data-fere-credit-trade]', moneyFromCents(snapshot.tradeCents));
      setText(root, '[data-fere-credit-cash]', moneyFromCents(snapshot.cashCents));
      setText(root, '[data-fere-credit-extra]', moneyFromCents(snapshot.extraTradeValueCents));
    }

    const qualification = root.querySelector('[data-fere-credit-qualification]');
    if (qualification) {
      qualification.textContent = root.dataset.marketState === 'stale'
        ? 'Last available estimate, not a live price. FERE will confirm current value in person. Applicable tax is additional.'
        : 'Estimated credit. Final value follows in-person verification. Applicable tax is additional.';
    }
    root.querySelectorAll('[data-fere-ring-card]').forEach((card) => renderRingCard(root, card));
    renderSelectedRing(root);
  };

  const requestLabel = (path) => ({
    'cash-evaluation': 'Cash evaluation',
    'selected-ring-trade': 'Selected-ring trade',
    'custom-ring-trade': 'Custom-ring trade',
    'trade-consultation': 'Trade consultation',
  }[path] || 'Trade consultation');

  const currentPath = (root) => TradeCore.resolveJourneyPath({
    mode: root.dataset.goldMode,
    requestedPath: root.fereRequestPath,
    hasRing: Boolean(root.fereSelectedRing),
  });

  const syncContactGoldFields = (root, estimate = root.fereGoldSnapshot) => {
    const calculatorKarat = root.querySelector('[data-fere-gold-calc-karat]');
    const calculatorWeight = root.querySelector('[data-fere-gold-calc-weight]');
    const formKarat = root.querySelector('[data-fere-form-karat]');
    const formWeight = root.querySelector('[data-fere-form-weight]');
    if (formKarat && calculatorKarat && formKarat.value !== calculatorKarat.value) formKarat.value = calculatorKarat.value;
    if (formWeight && calculatorWeight && formWeight.value !== calculatorWeight.value) {
      formWeight.value = estimate?.weightGrams ?? calculatorWeight.value;
    }
  };

  const safeSessionStorage = () => {
    try {
      return window.sessionStorage;
    } catch (_error) {
      return null;
    }
  };

  const restoreRuntimeQuote = (root, context) => {
    const quote = context?.quote;
    if (!quote) return;
    const provider = quote.provider === PROVIDERS.goldApi.id
      ? PROVIDERS.goldApi
      : quote.provider === PROVIDERS.xaus.id
        ? PROVIDERS.xaus
        : null;
    const quoteTimestampMs = Date.parse(quote.quoteTimestamp);
    if (!Number.isFinite(quoteTimestampMs)) return;
    root.fereLastQuote = {
      providerId: provider?.id || quote.provider,
      providerLabel: provider?.label || quote.source,
      source: quote.source,
      symbol: 'XAU',
      currency: 'USD',
      unit: 'troy_oz',
      spot: quote.spotUsd,
      quoteTimestamp: quote.quoteTimestamp,
      quoteTimestampMs,
      responseTimestamp: null,
      responseTimestampMs: null,
      providerState: quote.status,
      providerAgeSeconds: null,
    };
  };

  const restoreContactFieldsFromBody = (root) => {
    if (!root.querySelector('[data-fere-gold-form-errors]')) return null;
    const form = root.querySelector('#FereSellGoldForm');
    const bodyInput = form?.querySelector('[data-fere-contact-body]');
    const body = String(bodyInput?.value || '').replace(/\r\n?/g, '\n');
    if (!/^FERE GOLD (?:CASH EVALUATION|AND RING REQUEST): UNVERIFIED ESTIMATE(?:S)?$/m.test(body)) return null;

    const readLine = (label) => {
      const prefix = `${label}:`;
      const line = body.split('\n').find((candidate) => candidate.startsWith(prefix));
      return line ? line.slice(prefix.length).trim() : '';
    };
    const setSelect = (selector, value) => {
      const select = form.querySelector(selector);
      if (!select) return;
      select.value = Array.from(select.options).some((option) => option.value === value) ? value : '';
    };

    const preferredContact = readLine('Preferred contact');
    const itemType = readLine('Item type');
    const description = readLine('Description');
    const weightLabel = readLine('Entered payable gold weight');
    const karatLabel = readLine('Karat');
    const notesMatch = body.match(/^Notes:\s*([\s\S]*?)\n\nESTIMATE\n/m);
    const notes = String(notesMatch?.[1] || '').trim();
    const recoveredPath = readLine('Path');
    const additionalBudget = readLine('Optional additional cash budget');

    setSelect('[name="contact[Preferred contact method]"]', preferredContact === 'Unknown' ? '' : preferredContact);
    setSelect('[name="contact[Item type]"]', itemType === 'Unknown' ? '' : itemType);
    const descriptionInput = form.querySelector('[name="contact[Item description]"]');
    if (descriptionInput) descriptionInput.value = description === 'Unknown' ? '' : description;
    const notesInput = form.querySelector('[name="contact[Additional notes]"]');
    if (notesInput) notesInput.value = notes === 'None provided' ? '' : notes;

    const formWeight = form.querySelector('[data-fere-form-weight]');
    const calculatorWeight = root.querySelector('[data-fere-gold-calc-weight]');
    const rawWeight = weightLabel.endsWith(' grams') ? weightLabel.slice(0, -6).trim() : '';
    const validWeight = TradeCore.calculateEstimate({ spotUsd: 3110.34768, weightGrams: rawWeight, karat: 14 }) !== null;
    const recoveredWeight = validWeight ? rawWeight : '';
    if (formWeight) formWeight.value = recoveredWeight;
    if (calculatorWeight) calculatorWeight.value = recoveredWeight;

    const formKarat = form.querySelector('[data-fere-form-karat]');
    const calculatorKarat = root.querySelector('[data-fere-gold-calc-karat]');
    const recoveredKarat = karatLabel.endsWith('K') ? karatLabel.slice(0, -1) : karatLabel;
    const supportedKarat = TradeCore.KARATS.includes(Number(recoveredKarat));
    const formKaratValue = supportedKarat ? recoveredKarat : (recoveredKarat === 'Other' ? 'Other' : '');
    if (formKarat) formKarat.value = formKaratValue;
    if (calculatorKarat && supportedKarat) calculatorKarat.value = recoveredKarat;
    root.fereFormKaratOverride = !supportedKarat;
    const allowedPath = ['cash-evaluation', 'selected-ring-trade', 'trade-consultation'].includes(recoveredPath)
      ? recoveredPath
      : 'trade-consultation';
    const mode = allowedPath === 'cash-evaluation' ? 'cash' : 'trade';
    const formMode = form.querySelector('[data-fere-form-mode]');
    if (formMode) formMode.value = mode;

    const budget = root.querySelector('[data-fere-additional-budget]');
    if (budget && Array.from(budget.options).some((option) => option.value === additionalBudget)) {
      budget.value = additionalBudget;
    }
    if (mode === 'trade') {
      const productId = readLine('Product ID');
      const variantId = readLine('Variant ID');
      const card = Array.from(root.querySelectorAll('[data-fere-ring-card]'))
        .find((candidate) => candidate.dataset.productId === productId);
      const select = card?.querySelector('[data-fere-ring-variant]');
      if (select && Array.from(select.options).some((option) => option.value === variantId)) {
        select.value = variantId;
        root.fereSelectedRing = readRingCard(card);
        root.fereSelectedCard = card;
      }
    }

    const spotUsd = Number.parseFloat(readLine('Spot used in calculation'));
    const quoteTimestamp = readLine('Quote timestamp');
    const source = readLine('Source');
    const recoveredQuote = Number.isFinite(spotUsd) && quoteTimestamp && quoteTimestamp !== 'Unknown'
      ? {
          spotUsd,
          currency: 'USD',
          source: source && source !== 'Unknown' ? source : 'Returned contact request',
          provider: /gold-api/i.test(source) ? PROVIDERS.goldApi.id : /xaus/i.test(source) ? PROVIDERS.xaus.id : 'returned_form',
          status: /stale|last available/i.test(readLine('Status')) ? 'stale' : 'fresh',
          quoteTimestamp,
        }
      : null;
    const recoveredContext = TradeCore.createContext({
      path: allowedPath,
      mode,
      gold: { weightGrams: recoveredWeight || null, karat: supportedKarat ? recoveredKarat : null },
      quote: recoveredQuote,
      ring: mode === 'trade' ? root.fereSelectedRing : null,
      additionalBudget: mode === 'trade' ? additionalBudget : '',
    });
    restoreRuntimeQuote(root, recoveredContext);
    if (recoveredContext) TradeCore.writeContext(safeSessionStorage(), recoveredContext);
    return {
      path: allowedPath,
      mode,
      context: recoveredContext,
    };
  };

  const contextCandidate = (root, path = currentPath(root)) => {
    const snapshot = root.fereGoldSnapshot;
    const formKarat = root.querySelector('[data-fere-form-karat]');
    const formWeight = root.querySelector('[data-fere-form-weight]');
    const quote = snapshot ? {
      spotUsd: snapshot.quote.spot,
      currency: 'USD',
      source: snapshot.quote.source,
      provider: snapshot.quote.providerId,
      status: snapshot.quote.marketState,
      quoteTimestamp: snapshot.quote.quoteTimestamp,
    } : null;
    return {
      path,
      mode: root.dataset.goldMode,
      gold: {
        weightGrams: formWeight?.value || null,
        karat: formKarat?.value || null,
      },
      quote,
      ring: root.fereSelectedRing || null,
      additionalBudget: root.querySelector('[data-fere-additional-budget]')?.value || '',
    };
  };

  const persistTradeContext = (root, path = currentPath(root)) =>
    TradeCore.writeContext(safeSessionStorage(), contextCandidate(root, path));

  const updateContactPayload = (root) => {
    if (root.fereGoldSubmissionLocked) return;
    const form = root.querySelector('#FereSellGoldForm');
    if (!form || form.querySelector('[data-fere-gold-submit]') === null) return;
    const path = currentPath(root);
    const label = requestLabel(path);
    const snapshot = root.fereGoldSnapshot;
    const ring = path === 'cash-evaluation' ? null : root.fereSelectedRing;
    const contribution = snapshot && ring && ring.currency === 'USD'
      ? TradeCore.calculateContribution({ ringPriceCents: ring.priceCents, tradeCents: snapshot.tradeCents })
      : null;
    const rawWeight = root.querySelector('[data-fere-form-weight]')?.value.trim() || '';
    const rawKarat = root.querySelector('[data-fere-form-karat]')?.value || '';
    const additionalBudget = path === 'cash-evaluation'
      ? 'Not applicable'
      : (root.querySelector('[data-fere-additional-budget]')?.value || 'Not specified');
    const amountToAdd = path === 'cash-evaluation'
      ? 'Not applicable'
      : (contribution ? displayOrUnknown(contribution.additionalPaymentCents) : 'Unknown');

    setValue(root, 'requestType', label);
    setValue(root, 'path', path);
    setValue(root, 'estimateStatus', snapshot ? (snapshot.quote.marketState === 'fresh' ? 'Live' : 'Last available / stale') : 'Unavailable');
    setValue(root, 'spot', snapshot ? `${canonicalSpotUsd(snapshot.quote.spot)} USD per troy ounce` : 'Unknown');
    setValue(root, 'source', snapshot ? `${snapshot.quote.source} (${snapshot.quote.providerLabel})` : 'Unknown');
    setValue(root, 'quoteTimestamp', snapshot ? snapshot.quote.quoteTimestamp : 'Unknown');
    setValue(root, 'estimateCurrency', 'USD');
    setValue(root, 'market', snapshot ? displayWithCentsOrUnknown(snapshot.marketCents) : 'Unknown');
    setValue(root, 'cash', snapshot ? displayWithCentsOrUnknown(snapshot.cashCents) : 'Unknown');
    setValue(root, 'trade', snapshot ? displayWithCentsOrUnknown(snapshot.tradeCents) : 'Unknown');
    setValue(root, 'extraTrade', snapshot ? displayWithCentsOrUnknown(snapshot.extraTradeValueCents) : 'Unknown');
    setValue(root, 'ringTitle', ring?.title || 'Not applicable');
    setValue(root, 'productId', ring?.productId || 'Not applicable');
    setValue(root, 'variantId', ring?.variantId || 'Not applicable');
    setValue(root, 'sku', ring?.sku || 'Not applicable');
    setValue(root, 'ringOptions', ring?.options?.join(' · ') || 'Not applicable');
    setValue(root, 'ringPrice', ring ? `${formatCurrencyCents(ring.priceCents, ring.currency)} ${ring.currency} (${ring.priceCents} minor units)` : 'Not applicable');
    setValue(root, 'ringCurrency', ring?.currency || 'Not applicable');
    setValue(root, 'ringUrl', ring?.url ? `${window.location.origin}${ring.url}` : 'Not applicable');
    setValue(root, 'amountToAdd', contribution ? displayWithCentsOrUnknown(contribution.additionalPaymentCents) : amountToAdd);
    setValue(root, 'excessCredit', contribution?.unresolvedExcessCents > 0 ? displayWithCentsOrUnknown(contribution.unresolvedExcessCents) : 'Not applicable');
    setValue(root, 'additionalBudget', additionalBudget);

    const itemType = form.querySelector('[name="contact[Item type]"]')?.value || 'Unknown';
    const description = form.querySelector('[name="contact[Item description]"]')?.value.trim() || 'Unknown';
    const notes = form.querySelector('[name="contact[Additional notes]"]')?.value.trim() || 'None provided';
    const preferredContact = form.querySelector('[name="contact[Preferred contact method]"]')?.value || 'Unknown';
    const body = [
      path === 'cash-evaluation'
        ? 'FERE GOLD CASH EVALUATION: UNVERIFIED ESTIMATE'
        : 'FERE GOLD AND RING REQUEST: UNVERIFIED ESTIMATES',
      '',
      'REQUEST',
      `Type: ${label}`,
      `Origin: Sell Your Gold (${window.location.pathname})`,
      `Path: ${path}`,
      `Preferred contact: ${preferredContact}`,
      '',
      'GOLD',
      `Item type: ${itemType}`,
      `Description: ${description}`,
      `Entered payable gold weight: ${rawWeight ? `${rawWeight} grams` : 'Unknown'}`,
      `Karat: ${rawKarat ? (TradeCore.KARATS.includes(Number(rawKarat)) ? `${rawKarat}K` : rawKarat) : 'Unknown'}`,
      `Notes: ${notes}`,
      '',
      'ESTIMATE',
      `Status: ${snapshot ? (snapshot.quote.marketState === 'fresh' ? 'Live' : 'Last available / stale') : 'Unavailable'}`,
      `Spot used in calculation: ${snapshot ? `${canonicalSpotUsd(snapshot.quote.spot)} USD per troy ounce` : 'Unknown'}`,
      `Source: ${snapshot ? `${snapshot.quote.source} (${snapshot.quote.providerLabel})` : 'Unknown'}`,
      `Quote timestamp: ${snapshot ? snapshot.quote.quoteTimestamp : 'Unknown'}`,
      `Market reference: ${snapshot ? displayWithCentsOrUnknown(snapshot.marketCents) : 'Unknown'}`,
      `Cash estimate (85%): ${snapshot ? displayWithCentsOrUnknown(snapshot.cashCents) : 'Unknown'}`,
      `Trade estimate (90%): ${snapshot ? displayWithCentsOrUnknown(snapshot.tradeCents) : 'Unknown'}`,
      '',
      'RING',
      `Product: ${ring?.title || 'Not selected'}`,
      `Product ID: ${ring?.productId || 'Not applicable'}`,
      `Variant ID: ${ring?.variantId || 'Not applicable'}`,
      `SKU: ${ring?.sku || 'Not applicable'}`,
      `Options: ${ring?.options?.join(' · ') || 'Not applicable'}`,
      `Retail price: ${ring ? `${formatCurrencyCents(ring.priceCents, ring.currency)} ${ring.currency} (${ring.priceCents} minor units)` : 'Not applicable'}`,
      `Product URL: ${ring?.url ? `${window.location.origin}${ring.url}` : 'Not applicable'}`,
      '',
      'BUDGET',
      `Estimated amount to add before tax: ${contribution ? displayWithCentsOrUnknown(contribution.additionalPaymentCents) : amountToAdd}`,
      `Unresolved excess estimated credit: ${contribution?.unresolvedExcessCents > 0 ? displayWithCentsOrUnknown(contribution.unresolvedExcessCents) : 'Not applicable'}`,
      `Optional additional cash budget: ${additionalBudget}`,
      '',
      'All estimates are client-side and unverified. FERE must confirm purity, payable weight, product availability, current retail price, applicable tax, and the final transaction.',
    ].join('\n');
    const bodyInput = form.querySelector('[data-fere-contact-body]');
    if (bodyInput) bodyInput.value = body;

    const preview = root.querySelector('[data-fere-request-preview]');
    if (preview) {
      const ringSummary = ring ? `${ring.title}, ${ring.options.join(' · ')}` : 'No ring selected';
      preview.replaceChildren();
      const strong = document.createElement('strong');
      const span = document.createElement('span');
      strong.textContent = label;
      span.textContent = snapshot
        ? (path === 'cash-evaluation'
          ? `${snapshot.weightGrams.toLocaleString('en-US')} g of ${snapshot.karat}K · ${displayOrUnknown(snapshot.cashCents)} cash estimate`
          : `${snapshot.weightGrams.toLocaleString('en-US')} g of ${snapshot.karat}K · ${displayOrUnknown(snapshot.tradeCents)} trade · ${ringSummary}`)
        : `${goldCopy(root, 'currentEstimatePending')} · ${ringSummary}`;
      preview.append(strong, span);
    }
  };

  const setGoldMode = (root, mode, requestedPath = '', shouldPersist = true) => {
    root.dataset.goldMode = mode === 'cash' ? 'cash' : 'trade';
    const formMode = root.querySelector('[data-fere-form-mode]');
    if (formMode) formMode.value = root.dataset.goldMode;
    root.querySelectorAll('[data-fere-gold-mode-button]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.fereGoldModeButton === root.dataset.goldMode));
    });
    const tradePanel = root.querySelector('[data-fere-trade-panel]');
    if (tradePanel) tradePanel.hidden = root.dataset.goldMode === 'cash';
    root.fereRequestPath = TradeCore.resolveJourneyPath({
      mode: root.dataset.goldMode,
      requestedPath,
      hasRing: Boolean(root.fereSelectedRing),
    });
    updateContactPayload(root);
    if (shouldPersist) persistTradeContext(root);
  };

  const selectRing = (root, card) => {
    const ring = readRingCard(card);
    if (!ring) return;
    root.fereSelectedRing = ring;
    root.fereSelectedCard = card;
    setGoldMode(root, 'trade', 'selected-ring-trade', false);
    renderTradeJourney(root);
    updateContactPayload(root);
    persistTradeContext(root);
    scrollIntoViewRespectingMotion(root.querySelector('[data-fere-ring-selection]'), 'center');
    root.querySelector('[data-fere-selected-ring-title]')?.focus({ preventScroll: true });
  };

  const restoreTradeContext = (root) => {
    const context = TradeCore.readContext(safeSessionStorage());
    if (!context) return;
    restoreRuntimeQuote(root, context);
    const weight = root.querySelector('[data-fere-gold-calc-weight]');
    const karat = root.querySelector('[data-fere-gold-calc-karat]');
    const formWeight = root.querySelector('[data-fere-form-weight]');
    const formKarat = root.querySelector('[data-fere-form-karat]');
    if (context.gold.weightGrams !== null) {
      if (weight) weight.value = String(context.gold.weightGrams);
      if (formWeight) formWeight.value = String(context.gold.weightGrams);
    }
    if (context.gold.karat !== null) {
      if (karat) karat.value = String(context.gold.karat);
      if (formKarat) formKarat.value = String(context.gold.karat);
      root.fereFormKaratOverride = false;
    } else {
      if (formKarat) formKarat.value = '';
      root.fereFormKaratOverride = true;
    }
    const budget = root.querySelector('[data-fere-additional-budget]');
    if (budget && Array.from(budget.options).some((option) => option.value === context.additionalBudget)) {
      budget.value = context.additionalBudget;
    }
    if (context.ring) {
      const card = Array.from(root.querySelectorAll('[data-fere-ring-card]'))
        .find((candidate) => candidate.dataset.productId === context.ring.productId);
      const select = card?.querySelector('[data-fere-ring-variant]');
      if (select && Array.from(select.options).some((option) => option.value === context.ring.variantId)) {
        select.value = context.ring.variantId;
        root.fereSelectedRing = readRingCard(card);
        root.fereSelectedCard = card;
      }
    }
    return context;
  };

  const initTradeJourney = (root) => {
    const restoredContext = restoreTradeContext(root);
    const recoveredFormContext = restoreContactFieldsFromBody(root);
    const success = root.querySelector('[data-fere-gold-success]');
    if (success) {
      window.requestAnimationFrame(() => {
        success.focus({ preventScroll: true });
        scrollIntoViewRespectingMotion(success, 'center');
      });
    }
    root.querySelectorAll('[data-fere-gold-mode-button]').forEach((button) => {
      button.addEventListener('click', () => setGoldMode(root, button.dataset.fereGoldModeButton));
    });
    root.querySelectorAll('[data-fere-gold-mode-action]').forEach((action) => {
      action.addEventListener('click', () => setGoldMode(root, action.dataset.fereGoldModeAction));
    });
    const ringCards = Array.from(root.querySelectorAll('[data-fere-ring-card]'));
    const ringRail = root.querySelector('[data-fere-ring-rail]');
    const expandRings = root.querySelector('[data-fere-expand-rings]');
    if (ringRail && expandRings && ringCards.length > 6) {
      ringRail.dataset.collapsed = 'true';
      expandRings.hidden = false;
      expandRings.addEventListener('click', () => {
        const isExpanded = expandRings.getAttribute('aria-expanded') !== 'true';
        expandRings.setAttribute('aria-expanded', String(isExpanded));
        expandRings.textContent = goldCopy(root, isExpanded ? 'showFewer' : 'viewAll');
        ringRail.dataset.collapsed = String(!isExpanded);
        ringRail.classList.toggle('is-expanded', isExpanded);
        if (isExpanded) {
          ringCards[6]?.querySelector('[data-fere-ring-variant]')?.focus({ preventScroll: true });
          scrollIntoViewRespectingMotion(ringCards[6], 'start');
        } else {
          scrollIntoViewRespectingMotion(expandRings, 'center');
        }
      });
    }
    const ringEmpty = root.querySelector('[data-fere-ring-empty]');
    if (ringEmpty) ringEmpty.hidden = ringCards.length > 0;
    ringCards.forEach((card) => {
      card.querySelector('[data-fere-ring-variant]')?.addEventListener('change', () => {
        const isSelectedCard = root.fereSelectedCard
          ? root.fereSelectedCard === card
          : root.fereSelectedRing?.productId === card.dataset.productId;
        if (isSelectedCard) root.fereSelectedRing = readRingCard(card);
        renderTradeJourney(root);
        updateContactPayload(root);
        persistTradeContext(root);
      });
      card.querySelector('[data-fere-ring-choose]')?.addEventListener('click', () => selectRing(root, card));
      renderRingCard(root, card);
    });
    root.querySelector('[data-fere-ring-change]')?.addEventListener('click', () => {
      root.fereSelectedRing = null;
      root.fereSelectedCard = null;
      root.fereRequestPath = 'trade-consultation';
      renderTradeJourney(root);
      updateContactPayload(root);
      persistTradeContext(root);
      const ringRail = root.querySelector('[data-fere-ring-rail]');
      scrollIntoViewRespectingMotion(ringRail, 'center');
      const nextRingControl = ringRail?.querySelector('[data-fere-ring-variant], [data-fere-ring-choose]')
        || root.querySelector('[data-fere-ring-empty] a');
      nextRingControl?.focus({ preventScroll: true });
    });
    root.querySelector('[data-fere-additional-budget]')?.addEventListener('change', () => {
      updateContactPayload(root);
      persistTradeContext(root);
    });
    root.querySelectorAll('[data-fere-request-path]').forEach((button) => {
      button.addEventListener('click', () => {
        setGoldMode(root, 'trade', button.dataset.fereRequestPath);
        scrollIntoViewRespectingMotion(root.querySelector('#FereSellGoldForm'));
      });
    });
    root.querySelectorAll('[data-fere-custom-handoff]').forEach((link) => {
      link.addEventListener('click', () => {
        root.fereRequestPath = 'custom-ring-trade';
        persistTradeContext(root, 'custom-ring-trade');
      });
    });

    const calculatorKarat = root.querySelector('[data-fere-gold-calc-karat]');
    const calculatorWeight = root.querySelector('[data-fere-gold-calc-weight]');
    const formKarat = root.querySelector('[data-fere-form-karat]');
    const formWeight = root.querySelector('[data-fere-form-weight]');
    formKarat?.addEventListener('change', () => {
      if (TradeCore.KARATS.includes(Number(formKarat.value))) {
        root.fereFormKaratOverride = false;
        calculatorKarat.value = formKarat.value;
        renderCalculator(root);
      } else {
        root.fereFormKaratOverride = true;
        clearCalculator(root, 'empty', 'Choose a supported karat in the calculator for an online estimate, or send the item as not sure.');
      }
      persistTradeContext(root);
    });
    formWeight?.addEventListener('input', () => {
      calculatorWeight.value = formWeight.value;
      if (!TradeCore.KARATS.includes(Number(formKarat?.value))) {
        root.fereFormKaratOverride = true;
        clearCalculator(root, 'empty', 'Choose a supported karat in the calculator for an online estimate, or send the item as not sure.');
      } else {
        root.fereFormKaratOverride = false;
        calculatorKarat.value = formKarat.value;
        renderCalculator(root);
      }
      persistTradeContext(root);
    });

    const form = root.querySelector('#FereSellGoldForm');
    const formMode = root.querySelector('[data-fere-form-mode]');
    formMode?.addEventListener('change', () => {
      if (formMode.value === 'cash' || formMode.value === 'trade') {
        setGoldMode(root, formMode.value);
      }
    });
    form?.addEventListener('input', () => updateContactPayload(root));
    form?.addEventListener('change', () => updateContactPayload(root));
    form?.addEventListener('submit', (event) => {
      updateContactPayload(root);
      if (form.dataset.fereSubmitting === 'true') {
        event.preventDefault();
        return;
      }
      if (!form.checkValidity()) return;
      root.fereSubmittedSnapshot = root.fereGoldSnapshot;
      root.fereGoldSubmissionLocked = true;
      form.dataset.fereSubmitting = 'true';
      const submit = form.querySelector('[data-fere-gold-submit]');
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-disabled', 'true');
        submit.textContent = 'Sending Request…';
      }
    });
    root.fereGoldSubmissionPageShowHandler = (event) => {
      if (!event.persisted || !form) return;
      root.fereGoldSubmissionLocked = false;
      root.fereSubmittedSnapshot = null;
      delete form.dataset.fereSubmitting;
      const submit = form.querySelector('[data-fere-gold-submit]');
      if (submit) {
        submit.disabled = false;
        submit.setAttribute('aria-disabled', 'false');
        submit.textContent = 'Send My Request';
      }
      renderCalculator(root);
      updateContactPayload(root);
    };
    window.addEventListener('pageshow', root.fereGoldSubmissionPageShowHandler);
    setGoldMode(
      root,
      recoveredFormContext?.mode || restoredContext?.mode || root.dataset.goldMode,
      recoveredFormContext?.path || restoredContext?.path || '',
      false,
    );
    renderTradeJourney(root);
    updateContactPayload(root);
  };

  const setUnavailable = (root, code = 'unavailable', details = {}) => {
    if (root.fereGoldSubmissionLocked) return;
    clearPriceExpiry(root);
    root.fereLastQuote = null;
    root.dataset.state = 'unavailable';
    root.dataset.marketState = 'unavailable';
    root.querySelector('[data-fere-gold-values]')?.setAttribute('hidden', '');

    setText(root, '[data-fere-gold-spot]', goldCopy(root, 'pricePending'));
    setTextAll(root, '[data-fere-gold-updated]', goldCopy(root, 'pricePending'));
    updateProviderAttribution(root);
    root.querySelectorAll('[data-fere-gold-market], [data-fere-gold-cash], [data-fere-gold-trade]').forEach((element) => {
      element.textContent = goldCopy(root, 'pricePending');
    });

    [
      'provider',
      'source',
      'currency',
      'unit',
      'spotUsd',
      'updatedAt',
      'priceAsOf',
      'responseUpdatedAt',
      'providerAgeSeconds',
    ].forEach((key) => delete root.dataset[key]);

    updateStateCopy(root, 'unavailable');
    setText(root, '[data-fere-gold-status]', goldCopy(root, 'marketPending'));
    renderCalculator(root);
    setDiagnostic(root, code, details);
  };

  const parseTimestamp = (value, code, provider) => {
    if (typeof value !== 'string' || !value.trim()) {
      throw new GoldPriceError(code, { provider });
    }
    const milliseconds = Date.parse(value);
    if (!Number.isFinite(milliseconds)) throw new GoldPriceError(code, { provider });
    return { value, milliseconds };
  };

  const validateTimestampAge = (timestamp, now, provider) => {
    const ageMs = now - timestamp.milliseconds;
    if (ageMs < 0) throw new GoldPriceError('future_timestamp', { provider });
    if (ageMs > MAX_STALE_AGE_MS) throw new GoldPriceError('unsafe_age', { provider });
    return ageMs;
  };

  const validateGoldApiPayload = (payload, now = Date.now()) => {
    const provider = PROVIDERS.goldApi;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new GoldPriceError('malformed_payload', { provider: provider.id });
    }

    const symbol = String(payload.symbol || '').toUpperCase();
    const currency = String(payload.currency || '').toUpperCase();
    const spot = payload.price;
    const timestamp = parseTimestamp(payload.updatedAt, 'missing_timestamp', provider.id);
    const [minimumSpot, maximumSpot] = VALID_SPOT_RANGE_USD;

    if (symbol !== 'XAU') throw new GoldPriceError('unexpected_symbol', { provider: provider.id });
    if (currency !== 'USD') throw new GoldPriceError('unexpected_currency', { provider: provider.id });
    if (!isFiniteNumber(spot) || spot < minimumSpot || spot > maximumSpot) {
      throw new GoldPriceError('invalid_spot_price', { provider: provider.id });
    }

    const quoteAgeMs = validateTimestampAge(timestamp, now, provider.id);
    return {
      providerId: provider.id,
      providerLabel: provider.label,
      source: provider.label,
      symbol,
      currency,
      unit: 'troy_oz',
      spot,
      quoteTimestamp: timestamp.value,
      quoteTimestampMs: timestamp.milliseconds,
      responseTimestamp: null,
      responseTimestampMs: null,
      providerState: quoteAgeMs <= MAX_FRESH_AGE_MS ? 'fresh' : 'stale',
      providerAgeSeconds: null,
    };
  };

  const validateXausPayload = (payload, now = Date.now()) => {
    const provider = PROVIDERS.xaus;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new GoldPriceError('malformed_payload', { provider: provider.id });
    }

    const rawXauSpot = payload?.xau?.price;
    const rawUsdSpot = payload?.spot_usd_oz;
    const hasXauSpot = Boolean(payload?.xau && hasOwn(payload.xau, 'price'));
    const hasUsdSpot = hasOwn(payload, 'spot_usd_oz');
    const symbol = String(payload.symbol || (payload.xau ? 'XAU' : '')).toUpperCase();
    const currency = String(payload?.xau?.currency || '').toUpperCase();
    const unit = String(payload?.xau?.unit || '').toLowerCase();
    const providerState = String(payload?.data_state?.status || '').toLowerCase();
    const rawProviderAgeSeconds = payload?.data_state?.age_seconds;
    const quoteTimestamp = parseTimestamp(
      payload?.data_state?.as_of || payload?.price_as_of,
      'missing_timestamp',
      provider.id,
    );
    const responseTimestamp = parseTimestamp(payload?.updated_at, 'missing_response_timestamp', provider.id);
    const [minimumSpot, maximumSpot] = VALID_SPOT_RANGE_USD;

    if (symbol !== 'XAU') throw new GoldPriceError('unexpected_symbol', { provider: provider.id });
    if (currency !== 'USD') throw new GoldPriceError('unexpected_currency', { provider: provider.id });
    if (!['troy_oz', 'oz'].includes(unit)) throw new GoldPriceError('unexpected_unit', { provider: provider.id });
    if (providerState === 'unavailable') throw new GoldPriceError('provider_unavailable', { provider: provider.id });
    if (!['fresh', 'stale'].includes(providerState)) {
      throw new GoldPriceError('invalid_provider_state', { provider: provider.id });
    }
    if (!hasXauSpot && !hasUsdSpot) throw new GoldPriceError('invalid_spot_price', { provider: provider.id });
    if ((hasXauSpot && !isFiniteNumber(rawXauSpot)) || (hasUsdSpot && !isFiniteNumber(rawUsdSpot))) {
      throw new GoldPriceError('invalid_spot_price', { provider: provider.id });
    }

    const spot = hasXauSpot ? rawXauSpot : rawUsdSpot;
    if (spot < minimumSpot || spot > maximumSpot) {
      throw new GoldPriceError('invalid_spot_price', { provider: provider.id });
    }
    if (hasXauSpot && hasUsdSpot && Math.abs(rawXauSpot - rawUsdSpot) > 0.05) {
      throw new GoldPriceError('conflicting_spot_prices', { provider: provider.id });
    }

    const quoteAgeMs = validateTimestampAge(quoteTimestamp, now, provider.id);
    validateTimestampAge(responseTimestamp, now, provider.id);
    if (responseTimestamp.milliseconds < quoteTimestamp.milliseconds) {
      throw new GoldPriceError('inconsistent_timestamps', { provider: provider.id });
    }

    return {
      providerId: provider.id,
      providerLabel: provider.label,
      source: String(payload?.price_source || payload?.data_state?.source || payload?.source || provider.label),
      symbol,
      currency,
      unit,
      spot,
      quoteTimestamp: quoteTimestamp.value,
      quoteTimestampMs: quoteTimestamp.milliseconds,
      responseTimestamp: responseTimestamp.value,
      responseTimestampMs: responseTimestamp.milliseconds,
      providerState: providerState === 'stale' || quoteAgeMs > MAX_FRESH_AGE_MS ? 'stale' : 'fresh',
      providerAgeSeconds: isFiniteNumber(rawProviderAgeSeconds) ? rawProviderAgeSeconds : null,
    };
  };

  const assessQuote = (quote, now = Date.now()) => {
    if (!quote || !isFiniteNumber(quote.spot) || !isFiniteNumber(quote.quoteTimestampMs)) {
      throw new GoldPriceError('invalid_normalized_quote', { provider: quote?.providerId });
    }
    const quoteAgeMs = now - quote.quoteTimestampMs;
    if (quoteAgeMs < 0) throw new GoldPriceError('future_timestamp', { provider: quote.providerId });
    if (quoteAgeMs > MAX_STALE_AGE_MS) throw new GoldPriceError('unsafe_age', { provider: quote.providerId });

    let responseAgeMs = null;
    if (isFiniteNumber(quote.responseTimestampMs)) {
      responseAgeMs = now - quote.responseTimestampMs;
      if (responseAgeMs < 0) throw new GoldPriceError('future_timestamp', { provider: quote.providerId });
      if (responseAgeMs > MAX_STALE_AGE_MS) throw new GoldPriceError('unsafe_age', { provider: quote.providerId });
    }

    return {
      ...quote,
      quoteAgeMs,
      responseAgeMs,
      marketState: quote.providerState === 'stale' || quoteAgeMs > MAX_FRESH_AGE_MS ? 'stale' : 'fresh',
    };
  };

  const formatChicagoTime = (timestamp) => new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Chicago',
    timeZoneName: 'short',
  }).format(new Date(timestamp));

  const schedulePriceExpiry = (root, assessed) => {
    clearPriceExpiry(root);
    if (root.fereGoldDisposed) return;
    const thresholdMs = assessed.marketState === 'fresh' ? MAX_FRESH_AGE_MS : MAX_STALE_AGE_MS;
    const delayMs = Math.max(1, thresholdMs - assessed.quoteAgeMs + 1);

    root.fereGoldExpiryTimer = window.setTimeout(() => {
      root.fereGoldExpiryTimer = null;
      if (root.fereGoldDisposed || root.isConnected === false || !root.fereLastQuote) return;
      try {
        renderQuote(root, root.fereLastQuote, 0);
      } catch (error) {
        const failure = error instanceof GoldPriceError ? error : new GoldPriceError('unknown_failure');
        setUnavailable(root, failure.code, {
          provider: failure.provider,
          attempt: 0,
          httpStatus: failure.status,
        });
      }
    }, delayMs);
  };

  const renderQuote = (root, quote, attempt = 1) => {
    if (root.fereGoldSubmissionLocked) return;
    const assessed = assessQuote(quote);

    setText(root, '[data-fere-gold-spot]', money.format(assessed.spot));

    KARATS.forEach((karat) => {
      const estimate = TradeCore.calculateEstimate({ spotUsd: assessed.spot, weightGrams: 1, karat });
      if (!estimate) throw new GoldPriceError('calculation_failure', { provider: assessed.providerId });
      setText(root, `[data-fere-gold-market="${karat}"]`, moneyFromCents(estimate.marketCents));
      setText(root, `[data-fere-gold-cash="${karat}"]`, moneyFromCents(estimate.cashCents));
      setText(root, `[data-fere-gold-trade="${karat}"]`, moneyFromCents(estimate.tradeCents));
    });

    const formattedTime = formatChicagoTime(assessed.quoteTimestamp);
    const timestampLabel = assessed.marketState === 'stale'
      ? `Last market update ${formattedTime}`
      : `Updated ${formattedTime}`;
    setTextAll(root, '[data-fere-gold-updated]', timestampLabel);
    updateProviderAttribution(root, assessed);

    root.fereLastQuote = quote;
    root.dataset.state = assessed.marketState === 'fresh' ? 'ready' : 'stale';
    root.dataset.marketState = assessed.marketState;
    root.dataset.provider = assessed.providerLabel;
    root.dataset.source = assessed.source;
    root.dataset.currency = assessed.currency;
    root.dataset.unit = assessed.unit;
    root.dataset.spotUsd = String(assessed.spot);
    root.dataset.updatedAt = assessed.quoteTimestamp;
    root.dataset.priceAsOf = assessed.quoteTimestamp;
    if (assessed.responseTimestamp) {
      root.dataset.responseUpdatedAt = assessed.responseTimestamp;
    } else {
      delete root.dataset.responseUpdatedAt;
    }
    root.dataset.providerAgeSeconds = assessed.providerAgeSeconds === null ? '' : String(assessed.providerAgeSeconds);
    root.dataset.troyOunceGrams = String(TROY_OUNCE_GRAMS);
    root.dataset.cashFactor = String(CASH_FACTOR);
    root.dataset.tradeFactor = String(TRADE_FACTOR);
    root.dataset.refreshIntervalMs = String(REFRESH_INTERVAL_MS);
    root.dataset.maximumStaleAgeHours = String(MAX_STALE_AGE_MS / (60 * 60 * 1000));
    root.querySelector('[data-fere-gold-values]')?.removeAttribute('hidden');

    updateStateCopy(root, assessed.marketState);
    setText(
      root,
      '[data-fere-gold-status]',
      assessed.marketState === 'stale'
        ? `Last available market price shown. ${timestampLabel}. This is not a live price.`
        : 'Live gold prices and estimates updated.',
    );
    setDiagnostic(root, assessed.marketState === 'fresh' ? 'success_fresh' : 'success_stale', {
      provider: assessed.providerId,
      attempt,
      httpStatus: 200,
      quoteAgeMs: assessed.quoteAgeMs,
    });
    schedulePriceExpiry(root, assessed);
    renderCalculator(root);
  };

  const acceptQuote = (root, candidate, attempt) => {
    const current = root.fereLastQuote;
    if (current) {
      if (candidate.quoteTimestampMs < current.quoteTimestampMs) {
        renderQuote(root, current, 0);
        setDiagnostic(root, 'ignored_older_quote', {
          provider: candidate.providerId,
          attempt,
          httpStatus: 200,
        });
        return false;
      }

      if (candidate.quoteTimestampMs === current.quoteTimestampMs) {
        if (Math.abs(candidate.spot - current.spot) > 0.005) {
          throw new GoldPriceError('conflicting_same_timestamp', { provider: candidate.providerId });
        }
        renderQuote(root, current, 0);
        setDiagnostic(root, 'quote_unchanged', {
          provider: candidate.providerId,
          attempt,
          httpStatus: 200,
        });
        return false;
      }
    }

    renderQuote(root, candidate, attempt);
    return true;
  };

  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const requestJson = async (root, provider) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const requestUrl = new URL(provider.endpoint);
    if (provider.id === PROVIDERS.xaus.id) {
      requestUrl.searchParams.set('fresh', String(Math.floor(Date.now() / 30000)));
    }
    root.fereGoldAbortControllers?.add(controller);

    try {
      const response = await fetch(requestUrl.toString(), {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        signal: controller.signal,
      });

      if (!response.ok) {
        let responseCode = '';
        if (provider.id === PROVIDERS.xaus.id && response.status === 503) {
          try {
            const failurePayload = await response.json();
            responseCode = String(failurePayload?.error || '').toLowerCase();
          } catch (error) {
            if (error?.name === 'AbortError') throw error;
          }
        }

        if (provider.id === PROVIDERS.xaus.id && response.status === 503 && responseCode === 'usage_exceeded') {
          throw new GoldPriceError('usage_exceeded', {
            provider: provider.id,
            transient: false,
            status: response.status,
          });
        }

        const transient = [408, 425, 429].includes(response.status) || response.status >= 500;
        throw new GoldPriceError(`http_${response.status}`, {
          provider: provider.id,
          transient,
          status: response.status,
        });
      }

      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        throw new GoldPriceError('malformed_json', { provider: provider.id, transient: true });
      }

      return payload;
    } catch (error) {
      if (error instanceof GoldPriceError) throw error;
      if (error?.name === 'AbortError') {
        throw new GoldPriceError('timeout', { provider: provider.id, transient: true });
      }
      throw new GoldPriceError('network_or_cors', { provider: provider.id, transient: true });
    } finally {
      window.clearTimeout(timeout);
      root.fereGoldAbortControllers?.delete(controller);
    }
  };

  const requestGoldApiWithRetries = async (root) => {
    let lastFailure = null;

    for (let attemptIndex = 0; attemptIndex <= PRIMARY_RETRY_DELAYS_MS.length; attemptIndex += 1) {
      if (root.fereGoldDisposed) return null;
      if (attemptIndex > 0) {
        await wait(PRIMARY_RETRY_DELAYS_MS[attemptIndex - 1]);
        if (root.fereGoldDisposed) return null;
      }

      const attempt = attemptIndex + 1;
      try {
        const payload = await requestJson(root, PROVIDERS.goldApi);
        return { quote: validateGoldApiPayload(payload), attempt };
      } catch (error) {
        lastFailure = error instanceof GoldPriceError
          ? error
          : new GoldPriceError('unknown_failure', { provider: PROVIDERS.goldApi.id });
        setDiagnostic(root, lastFailure.code, {
          provider: PROVIDERS.goldApi.id,
          attempt,
          httpStatus: lastFailure.status,
        });

        const canRetry = lastFailure.transient && attemptIndex < PRIMARY_RETRY_DELAYS_MS.length;
        if (!canRetry) throw lastFailure;
      }
    }

    throw lastFailure || new GoldPriceError('unknown_failure', { provider: PROVIDERS.goldApi.id });
  };

  const requestXausOnce = async (root) => {
    if (xausDisabledForSession) {
      throw new GoldPriceError('usage_exceeded_session', { provider: PROVIDERS.xaus.id });
    }
    if (Date.now() < xausCooldownUntil) {
      throw new GoldPriceError('fallback_cooldown', { provider: PROVIDERS.xaus.id });
    }

    // One fallback attempt is eligible per normal ten-minute cycle, even when
    // it succeeds. Resume events therefore cannot repeatedly call XAUS.
    xausCooldownUntil = Date.now() + REFRESH_INTERVAL_MS;

    try {
      const payload = await requestJson(root, PROVIDERS.xaus);
      return { quote: validateXausPayload(payload), attempt: 1 };
    } catch (error) {
      const failure = error instanceof GoldPriceError
        ? error
        : new GoldPriceError('unknown_failure', { provider: PROVIDERS.xaus.id });
      if (root.fereGoldDisposed) throw failure;
      if (failure.code === 'usage_exceeded' || failure.code === 'network_or_cors') {
        // XAUS's current usage_exceeded 503 omits CORS, so browsers may expose
        // it only as network_or_cors. Do not retry that opaque failure in this
        // page session; Gold-API remains eligible on every normal cycle.
        xausDisabledForSession = true;
        xausCooldownUntil = Number.POSITIVE_INFINITY;
      } else {
        xausCooldownUntil = Math.max(xausCooldownUntil, Date.now() + XAUS_FAILURE_COOLDOWN_MS);
      }
      throw failure;
    }
  };

  const preserveLastQuoteOrSetUnavailable = (root, primaryFailure, fallbackFailure) => {
    root.dataset.goldPrimaryFailureCode = primaryFailure?.code || 'unknown_failure';
    root.dataset.goldFallbackFailureCode = fallbackFailure?.code || 'unknown_failure';

    if (root.fereLastQuote) {
      try {
        renderQuote(root, root.fereLastQuote, 0);
        setDiagnostic(root, 'refresh_failed_using_validated_quote', {
          provider: root.fereLastQuote.providerId,
          attempt: 0,
          httpStatus: fallbackFailure?.status || primaryFailure?.status || 0,
        });
        return;
      } catch (error) {
        // The stored quote is outside the safety window and must not remain visible.
      }
    }

    setUnavailable(root, 'both_providers_failed', {
      provider: fallbackFailure?.provider || primaryFailure?.provider || '',
      attempt: 0,
      httpStatus: fallbackFailure?.status || primaryFailure?.status || 0,
    });
  };

  const fetchPrice = (root) => {
    if (root.fereGoldDisposed) return Promise.resolve();
    if (root.fereGoldRequestPromise) return root.fereGoldRequestPromise;

    root.fereGoldRequestPromise = (async () => {
      const params = new URLSearchParams(window.location.search);
      const isPreview = params.has('preview_theme_id')
        || window.location.hostname.endsWith('.myshopify.com')
        || Boolean(document.querySelector('#PBarNextFrame'));
      if (isPreview && params.get('fere_gold_price_test') === 'unavailable') {
        setUnavailable(root, 'preview_forced_unavailable');
        return;
      }

      let primaryFailure = null;
      try {
        const primaryResult = await requestGoldApiWithRetries(root);
        if (primaryResult && !root.fereGoldDisposed) {
          acceptQuote(root, primaryResult.quote, primaryResult.attempt);
          delete root.dataset.goldPrimaryFailureCode;
          delete root.dataset.goldFallbackFailureCode;
        }
        return;
      } catch (error) {
        primaryFailure = error instanceof GoldPriceError
          ? error
          : new GoldPriceError('unknown_failure', { provider: PROVIDERS.goldApi.id });
        root.dataset.goldPrimaryFailureCode = primaryFailure.code;
      }

      let fallbackFailure = null;
      try {
        const fallbackResult = await requestXausOnce(root);
        if (fallbackResult && !root.fereGoldDisposed) {
          acceptQuote(root, fallbackResult.quote, fallbackResult.attempt);
          delete root.dataset.goldFallbackFailureCode;
        }
        return;
      } catch (error) {
        fallbackFailure = error instanceof GoldPriceError
          ? error
          : new GoldPriceError('unknown_failure', { provider: PROVIDERS.xaus.id });
        root.dataset.goldFallbackFailureCode = fallbackFailure.code;
        setDiagnostic(root, fallbackFailure.code, {
          provider: PROVIDERS.xaus.id,
          attempt: 1,
          httpStatus: fallbackFailure.status,
        });
      }

      if (!root.fereGoldDisposed) {
        preserveLastQuoteOrSetUnavailable(root, primaryFailure, fallbackFailure);
      }
    })().finally(() => {
      root.fereGoldRequestPromise = null;
    });

    return root.fereGoldRequestPromise;
  };

  const startRefreshTimer = (root) => {
    if (root.fereGoldDisposed) return;
    if (root.fereGoldRefreshTimer) window.clearInterval(root.fereGoldRefreshTimer);
    root.fereGoldRefreshTimer = window.setInterval(() => {
      if (root.fereGoldDisposed || root.isConnected === false) return;
      fetchPrice(root);
    }, REFRESH_INTERVAL_MS);
  };

  const revalidateStoredQuote = (root) => {
    if (!root.fereLastQuote) return;
    try {
      renderQuote(root, root.fereLastQuote, 0);
    } catch (error) {
      const failure = error instanceof GoldPriceError ? error : new GoldPriceError('unknown_failure');
      setUnavailable(root, failure.code, {
        provider: failure.provider,
        attempt: 0,
        httpStatus: failure.status,
      });
    }
  };

  const refreshAfterResume = (root) => {
    if (root.fereGoldDisposed || root.isConnected === false) return;
    revalidateStoredQuote(root);

    const now = Date.now();
    if (isFiniteNumber(root.fereGoldLastResumeFetchAt)
      && now - root.fereGoldLastResumeFetchAt < RESUME_DEDUPE_MS) return;

    root.fereGoldLastResumeFetchAt = now;
    startRefreshTimer(root);
    fetchPrice(root);
  };

  const cleanupRoot = (root) => {
    root.fereGoldDisposed = true;
    clearPriceExpiry(root);
    if (root.fereGoldRefreshTimer) window.clearInterval(root.fereGoldRefreshTimer);
    root.fereGoldRefreshTimer = null;
    root.fereGoldAbortControllers?.forEach((controller) => controller.abort());
    root.fereGoldAbortControllers?.clear();
    if (root.fereGoldVisibilityHandler) {
      document.removeEventListener('visibilitychange', root.fereGoldVisibilityHandler);
    }
    if (root.fereGoldPageShowHandler) {
      window.removeEventListener('pageshow', root.fereGoldPageShowHandler);
    }
    if (root.fereGoldSubmissionPageShowHandler) {
      window.removeEventListener('pageshow', root.fereGoldSubmissionPageShowHandler);
    }
    if (root.fereGoldSectionUnloadHandler) {
      document.removeEventListener('shopify:section:unload', root.fereGoldSectionUnloadHandler);
    }
  };

  const initRoot = (root) => {
    if (root.dataset.fereGoldInitialized === 'true') return;
    root.dataset.fereGoldInitialized = 'true';
    root.fereGoldDisposed = false;
    root.fereGoldAbortControllers = new Set();
    root.dataset.refreshIntervalMs = String(REFRESH_INTERVAL_MS);
    root.dataset.maximumStaleAgeHours = String(MAX_STALE_AGE_MS / (60 * 60 * 1000));
    initTradeJourney(root);
    revalidateStoredQuote(root);

    const karatInput = root.querySelector('[data-fere-gold-calc-karat]');
    const weightInput = root.querySelector('[data-fere-gold-calc-weight]');
    const calculateButton = root.querySelector('[data-fere-gold-calc-submit]');
    karatInput?.addEventListener('change', () => {
      root.fereFormKaratOverride = false;
      syncContactGoldFields(root, null);
      renderCalculator(root);
      persistTradeContext(root);
    });
    weightInput?.addEventListener('input', () => {
      root.fereFormKaratOverride = false;
      syncContactGoldFields(root, null);
      renderCalculator(root);
      persistTradeContext(root);
    });
    calculateButton?.addEventListener('click', () => {
      root.fereFormKaratOverride = false;
      syncContactGoldFields(root, null);
      renderCalculator(root);
      persistTradeContext(root);
    });

    root.fereGoldVisibilityHandler = () => {
      if (document.visibilityState === 'visible') refreshAfterResume(root);
    };
    root.fereGoldPageShowHandler = (event) => {
      if (event.persisted) refreshAfterResume(root);
    };
    root.fereGoldSectionUnloadHandler = (event) => {
      if (event.target === root || event.target?.contains?.(root)) cleanupRoot(root);
    };
    document.addEventListener('visibilitychange', root.fereGoldVisibilityHandler);
    window.addEventListener('pageshow', root.fereGoldPageShowHandler);
    document.addEventListener('shopify:section:unload', root.fereGoldSectionUnloadHandler);

    startRefreshTimer(root);
    fetchPrice(root);
  };

  const init = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-fere-live-gold]')) initRoot(scope);
    scope.querySelectorAll?.('[data-fere-live-gold]').forEach(initRoot);
  };

  window.fereGoldJourneyInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

